import { useChat } from '@ai-sdk/react';
import { CaretDown, PaperPlaneRight, Question, X } from '@phosphor-icons/react';
import { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from 'src/utils/className';
import { IconButton } from './IconButton';
import { Input } from './Input';
import { Tooltip } from './Tooltip';

type MintlifyAssistantProps = {
  domain: string;
  docsURL?: string;
  token: string;
  userId?: string;
  isOpen?: boolean;
  setIsOpen?: (isOpen: boolean) => void;
};

const MINTLIFY_API_BASE = 'https://api.mintlify.com/discovery/v1/assistant';

const normalizeMarkdownLinks = (text: string): string => {
  return text.replace(/\(([^)]+)\)\[([^\]]+)\]/g, '[$1]($2)');
};

const removeSuggestionsBlock = (text: string): string => {
  return text
    .replace(/```suggestions\n[\s\S]*?```/g, '')
    .replace(/```\n[\s\S]*?suggestions[\s\S]*?```/g, '')
    .trim();
};

export const MintlifyAssistant = ({
  domain,
  docsURL,
  token,
  userId,
  isOpen: controlledIsOpen,
  setIsOpen: controlledSetIsOpen,
}: MintlifyAssistantProps) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [isInputVisible, setIsInputVisible] = useState(true);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const isOpen =
    controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;
  const setIsOpen = controlledSetIsOpen || setInternalIsOpen;

  useEffect(() => {
    const savedThreadId = sessionStorage.getItem('mintlify-thread-id');
    if (savedThreadId) {
      setThreadId(savedThreadId);
    }
  }, []);

  const chat = useChat({
    api: `${MINTLIFY_API_BASE}/${domain}/message`,
    headers: {
      Authorization: `Bearer ${token}`,
    },
    experimental_prepareRequestBody: ({
      messages: chatMessages,
      requestBody,
    }) => {
      return {
        ...requestBody,
        fp: userId || 'anonymous',
        retrievalPageSize: 5,
        ...(threadId && { threadId }),
        messages: chatMessages.map((msg) => {
          const textParts = msg.parts?.filter(
            (part) => part.type === 'text',
          ) as Array<{ type: 'text'; text: string }> | undefined;
          const content =
            textParts?.map((part) => part.text).join('') ||
            (typeof msg.content === 'string' ? msg.content : '') ||
            '';

          return {
            id: msg.id,
            role: msg.role,
            content,
            parts: textParts || [{ type: 'text', text: content }],
          };
        }),
      };
    },
    onError: (error: Error) => {
      console.error('Error sending message to Mintlify:', error);
    },
  });

  const { messages, input, handleInputChange, handleSubmit, isLoading } = chat;

  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  return (
    <>
      {isOpen && (
        <div className="fixed right-20 bottom-4 w-96 bg-neutral-0 border border-neutral-200 rounded-lg shadow-lg flex flex-col z-50 transition-all duration-300 h-[600px]">
          <div className="flex items-center justify-between p-4 border-b border-neutral-200">
            <h3 className="text-lg font-medium text-neutral-800">
              Documentation Assistant
            </h3>
            <div className="flex items-center gap-2">
              <IconButton
                onClick={() => setIsOpen(false)}
                aria-label="Close assistant"
              >
                <X size={20} />
              </IconButton>
            </div>
          </div>

          <div
            ref={messagesContainerRef}
            className="flex-1 overflow-y-auto p-4 space-y-4"
          >
            {messages.length === 0 && (
              <div className="text-neutral-500 text-sm text-center py-8">
                Ask me anything about the documentation!
              </div>
            )}

            {messages.length > 0 &&
              messages.map((msg) => {
                const textParts = msg.parts?.filter(
                  (part) => part.type === 'text',
                ) as Array<{ type: 'text'; text: string }> | undefined;
                const content =
                  textParts?.map((part) => part.text).join('') || '';
                const isUser = msg.role === 'user';

                if (!isUser && !content.trim()) {
                  return null;
                }

                return (
                  <div
                    key={msg.id}
                    className={cn(
                      'flex',
                      isUser ? 'justify-end' : 'justify-start',
                    )}
                  >
                    <div
                      className={cn(
                        'max-w-[80%] rounded-lg px-4 py-2',
                        isUser
                          ? 'bg-primary text-neutral-0'
                          : 'bg-neutral-50 text-neutral-800 border border-neutral-200',
                      )}
                    >
                      <div className="text-sm prose prose-sm max-w-none overflow-x-auto">
                        {msg.role === 'assistant' ? (
                          <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            components={{
                              a: ({ href, ...props }) => {
                                let finalHref = href || '';
                                if (finalHref && docsURL) {
                                  if (finalHref.startsWith('/')) {
                                    const baseUrl = docsURL.endsWith('/')
                                      ? docsURL.slice(0, -1)
                                      : docsURL;
                                    finalHref = `${baseUrl}${finalHref}`;
                                  } else if (
                                    !finalHref.startsWith('http://') &&
                                    !finalHref.startsWith('https://')
                                  ) {
                                    const baseUrl = docsURL.endsWith('/')
                                      ? docsURL.slice(0, -1)
                                      : docsURL;
                                    finalHref = `${baseUrl}/${finalHref}`;
                                  }
                                }
                                return (
                                  <a
                                    {...props}
                                    href={finalHref}
                                    className="text-primary-600 hover:underline"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  />
                                );
                              },
                              strong: ({ ...props }) => (
                                <strong {...props} className="font-semibold" />
                              ),
                              ul: ({ ...props }) => (
                                <ul
                                  {...props}
                                  className="list-disc ml-4 my-2"
                                />
                              ),
                              ol: ({ ...props }) => (
                                <ol
                                  {...props}
                                  className="list-decimal ml-4 my-2"
                                />
                              ),
                              li: ({ ...props }) => (
                                <li {...props} className="my-1" />
                              ),
                              p: ({ ...props }) => (
                                <p {...props} className="my-2" />
                              ),
                            }}
                          >
                            {normalizeMarkdownLinks(
                              removeSuggestionsBlock(content),
                            )}
                          </ReactMarkdown>
                        ) : (
                          content
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-neutral-50 text-neutral-800 border border-neutral-200 rounded-lg px-4 py-2">
                  <div className="text-sm">Thinking...</div>
                </div>
              </div>
            )}
          </div>

          <div className="p-4 border-t border-neutral-200">
            <form onSubmit={handleSubmit}>
              <Input
                ref={inputRef}
                value={input}
                onChange={handleInputChange}
                placeholder="Ask a question..."
                disabled={isLoading}
                inputAction={() => handleSubmit()}
                icon={<PaperPlaneRight size={20} />}
              />
            </form>
          </div>
        </div>
      )}

      {!isInputVisible && (
        <div className="fixed right-20 bottom-0 z-40 -mb-2">
          <Tooltip
            content="If you need help, we're here for you"
            position="left"
          >
            <div
              className="bg-primary-500/65 rounded-t-lg px-2 py-1 cursor-pointer hover:bg-primary-600 transition-colors"
              onClick={() => setIsInputVisible(true)}
            >
              <Question className="text-neutral-0" size={20} />
            </div>
          </Tooltip>
        </div>
      )}

      {isInputVisible && !isOpen && (
        <div className="fixed right-20 bottom-4 z-40 flex flex-row items-stretch">
          <Tooltip content="Hide assistant" position="top">
            <div
              className="bg-primary-600 border border-neutral-200 rounded-l-lg px-4 py-3 flex items-center gap-3 shadow-lg cursor-pointer"
              onClick={() => setIsInputVisible(false)}
            >
              <CaretDown className="text-neutral-0" size={20} />
            </div>
          </Tooltip>

          <div
            onClick={() => setIsOpen(true)}
            className="bg-neutral-0 border border-l-0 border-neutral-200 rounded-r-lg px-4 py-3 flex items-center gap-3 hover:border-primary transition-colors cursor-text overflow-hidden shadow-lg w-56"
          >
            <Question size={20} className="text-neutral-400 flex-shrink-0" />
            <span className="text-neutral-400 text-sm flex-1">How do I...</span>
            <PaperPlaneRight
              size={20}
              className="text-neutral-400 flex-shrink-0"
            />
          </div>
        </div>
      )}
    </>
  );
};
